import { useState, useEffect, useCallback } from 'react'
import { MessageSquareText, Copy, Check, Download, Loader2, Search, Save, FileText, BookOpen } from 'lucide-react'
import { api } from '../../lib/api'
import { notify } from '../../lib/notify'
import type { QaThreadSummary, QaThreadDetail } from '../../lib/api'
import { QaThreadList } from './QaThreadList'
import { QaEmailInput } from './QaEmailInput'

export function QaEngine() {
  const [threads, setThreads] = useState<QaThreadSummary[]>([])
  const [loadingThreads, setLoadingThreads] = useState(true)
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  const [showNewEmail, setShowNewEmail] = useState(false)
  const [threadDetail, setThreadDetail] = useState<QaThreadDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [responseContent, setResponseContent] = useState<string | null>(null)
  const [loadingResponse, setLoadingResponse] = useState(false)
  const [copied, setCopied] = useState(false)
  const [savingToSolutions, setSavingToSolutions] = useState(false)

  // Load threads on mount
  useEffect(() => {
    loadThreads()
  }, [])

  // Load thread detail when selection changes
  useEffect(() => {
    if (selectedThreadId) {
      loadThreadDetail(selectedThreadId)
    } else {
      setThreadDetail(null)
      setResponseContent(null)
    }
  }, [selectedThreadId])

  async function loadThreads() {
    setLoadingThreads(true)
    try {
      const res = await api.solutions.qa.listThreads()
      setThreads(res.threads)
    } catch (err) {
      console.error('Failed to load threads:', err)
    } finally {
      setLoadingThreads(false)
    }
  }

  async function loadThreadDetail(threadId: string) {
    setLoadingDetail(true)
    setResponseContent(null)
    try {
      const detail = await api.solutions.qa.getThread(threadId)
      setThreadDetail(detail)

      // If thread has answered questions, load the compiled response
      const answeredCount = detail.questions.filter(
        (q) => q.answers && q.answers.some((a) => a.is_selected)
      ).length
      if (answeredCount > 0) {
        loadResponse(threadId)
      }
    } catch (err) {
      console.error('Failed to load thread detail:', err)
    } finally {
      setLoadingDetail(false)
    }
  }

  async function loadResponse(threadId: string) {
    setLoadingResponse(true)
    try {
      const res = await api.solutions.qa.getResponse(threadId)
      setResponseContent(res.content)
    } catch (err) {
      console.error('Failed to load response:', err)
    } finally {
      setLoadingResponse(false)
    }
  }

  const handleSelectThread = useCallback((id: string) => {
    setSelectedThreadId(id)
    setShowNewEmail(false)
  }, [])

  const handleNewThread = useCallback(() => {
    setSelectedThreadId(null)
    setShowNewEmail(true)
    setThreadDetail(null)
    setResponseContent(null)
  }, [])

  const handleThreadCreated = useCallback((threadId: string) => {
    setShowNewEmail(false)
    setSelectedThreadId(threadId)
    loadThreads()
  }, [])

  async function handleCopyResponse() {
    if (!responseContent) return
    try {
      await navigator.clipboard.writeText(responseContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      console.error('Failed to copy response')
    }
  }

  function handleDownloadResponse() {
    if (!responseContent) return
    const title = threadDetail?.title || 'qa-response'
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50)
    const date = new Date().toISOString().split('T')[0]
    const blob = new Blob([responseContent], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug}-${date}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function handleSaveToSolutions() {
    if (!responseContent) return
    setSavingToSolutions(true)
    try {
      const title = threadDetail?.title || 'Q&A Response'
      await api.solutions.save({
        title: `QA Response — ${title}`,
        content: responseContent,
        tags: ['qa-response', 'solutions-qa'],
      })
      notify.success('Saved to Solutions')
    } catch {
      notify.error('Failed to save')
    } finally {
      setSavingToSolutions(false)
    }
  }

  async function handleNotebookClick() {
    notify.error('Notebook integration not yet available in DOCTRINE')
  }


  // Determine what to show in center panel
  const showEmailInput = showNewEmail || (!selectedThreadId && !loadingThreads)
  const showThreadView = !!selectedThreadId && !showNewEmail

  // Determine if response preview should show
  const hasAnswers = threadDetail?.questions.some(
    (q) => q.answers && q.answers.length > 0
  )
  const showResponsePreview = showThreadView && hasAnswers

  return (
    <div className="flex h-full gap-4">
      {/* Left Sidebar: Thread List */}
      <div className="w-72 flex-shrink-0 glass-card-subtle !p-0 overflow-hidden">
        <QaThreadList
          threads={threads}
          selectedId={selectedThreadId}
          onSelect={handleSelectThread}
          onNew={handleNewThread}
          loading={loadingThreads}
        />
      </div>

      {/* Center Panel */}
      <div className="flex-1 min-w-0 glass-card !p-0 overflow-hidden">
        {showEmailInput ? (
          <QaEmailInput onThreadCreated={handleThreadCreated} />
        ) : showThreadView ? (
          <ThreadDetailView
            thread={threadDetail}
            loading={loadingDetail}
            onRefresh={() => selectedThreadId && loadThreadDetail(selectedThreadId)}
          />
        ) : null}
      </div>

      {/* Right Panel: Response Preview */}
      {showResponsePreview && (
        <div className="w-96 flex-shrink-0 glass-card !p-0 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquareText className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Response Preview</h3>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleCopyResponse}
                disabled={!responseContent}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded transition-colors disabled:opacity-50"
                title="Copy response"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={handleDownloadResponse}
                disabled={!responseContent}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded transition-colors disabled:opacity-50"
                title="Download as markdown"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleSaveToSolutions}
                disabled={!responseContent || savingToSolutions}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded transition-colors disabled:opacity-50"
                title="Save to Solutions"
              >
                <Save className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleNotebookClick}
                disabled={!responseContent}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded transition-colors disabled:opacity-50"
                title="Add to notebook"
              >
                <BookOpen className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {loadingResponse ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
              </div>
            ) : responseContent ? (
              <div className="prose prose-sm max-w-none text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-xs leading-relaxed">
                {responseContent}
              </div>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-8">
                Generate answers to see the compiled response here.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Internal sub-component for thread detail view ----

interface ThreadDetailViewProps {
  thread: QaThreadDetail | null
  loading: boolean
  onRefresh: () => void
}

function ThreadDetailView({ thread, loading, onRefresh }: ThreadDetailViewProps) {
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set())
  const [generatingAll, setGeneratingAll] = useState(false)
  const [copiedIds, setCopiedIds] = useState<Set<string>>(new Set())
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set())
  const [copiedAll, setCopiedAll] = useState(false)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-6 h-6 text-indigo-500 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading thread...</p>
        </div>
      </div>
    )
  }

  if (!thread) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <MessageSquareText className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Select a thread to view details</p>
        </div>
      </div>
    )
  }

  // ── Per-question actions ─────────────────────────────────────────────
  async function handleSearchAnswer(questionId: string) {
    setGeneratingIds((prev) => new Set(prev).add(questionId))
    try {
      await api.solutions.qa.generateAnswer(questionId)
      onRefresh()
    } catch (err) {
      console.error('Failed to generate answer:', err)
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev)
        next.delete(questionId)
        return next
      })
    }
  }

  async function handleCopyAnswer(questionId: string, text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedIds((prev) => new Set(prev).add(questionId))
      setTimeout(() => {
        setCopiedIds((prev) => {
          const next = new Set(prev)
          next.delete(questionId)
          return next
        })
      }, 2000)
    } catch {
      console.error('Failed to copy answer')
    }
  }

  function handleSaveAnswer(questionId: string) {
    setSavedIds((prev) => new Set(prev).add(questionId))
  }

  // ── Bulk actions ─────────────────────────────────────────────────────
  async function handleSearchAll() {
    if (!thread) return
    setGeneratingAll(true)
    try {
      await api.solutions.qa.generateAll(thread.id)
      onRefresh()
    } catch (err) {
      console.error('Failed to generate all answers:', err)
    } finally {
      setGeneratingAll(false)
    }
  }

  async function handleCopyAll() {
    if (!thread) return
    const parts = thread.questions
      .map((q, i) => {
        const answer = q.answers?.find((a) => a.is_selected) || q.answers?.[0]
        if (!answer) return null
        return `## Q${i + 1}: ${q.question_text}\n\n${answer.answer_md}`
      })
      .filter(Boolean)

    try {
      await navigator.clipboard.writeText(parts.join('\n\n---\n\n'))
      setCopiedAll(true)
      setTimeout(() => setCopiedAll(false), 2000)
    } catch {
      console.error('Failed to copy all answers')
    }
  }

  function handleDownloadMd() {
    if (!thread) return
    const parts = thread.questions
      .map((q, i) => {
        const answer = q.answers?.find((a) => a.is_selected) || q.answers?.[0]
        if (!answer) return null
        return `## Q${i + 1}: ${q.question_text}\n\n${answer.answer_md}`
      })
      .filter(Boolean)

    const content = `# ${thread.title || 'Q&A Response'}\n\n${parts.join('\n\n---\n\n')}`
    const slug = (thread.title || 'qa-response').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50)
    const date = new Date().toISOString().split('T')[0]
    const blob = new Blob([content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug}-${date}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function handleDownloadDocx() {
    if (!thread) return
    try {
      const resp = await fetch(`/api/solutions/qa/${thread.id}/response/docx`)
      if (!resp.ok) throw new Error('Download failed')
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const slug = (thread.title || 'qa-response').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50)
      a.download = `${slug}-${new Date().toISOString().split('T')[0]}.docx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download DOCX:', err)
    }
  }

  const answeredCount = thread.questions.filter(
    (q) => q.answers && q.answers.some((a) => a.is_selected)
  ).length
  const hasAnyAnswers = answeredCount > 0

  const btnBase =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors'
  const btnPrimary = `${btnBase} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed`
  const btnSecondary = `${btnBase} border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10`
  const btnGreen = `${btnBase} bg-emerald-600 text-white hover:bg-emerald-700`

  return (
    <div className="h-full flex flex-col">
      {/* Thread header */}
      <div className="px-5 py-4 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {thread.title || 'Untitled Thread'}
            </h2>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
              {thread.sender_name && <span>From: {thread.sender_name}</span>}
              {thread.customer_name && <span>Customer: {thread.customer_name}</span>}
              <span>
                {thread.questions.length} question{thread.questions.length !== 1 ? 's' : ''} ·{' '}
                {answeredCount} answered
              </span>
            </div>
          </div>
          <span
            className={`ml-3 px-2 py-0.5 text-[10px] font-medium rounded ${
              thread.status === 'complete'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                : thread.status === 'extracted'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                  : thread.status === 'reviewed'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400'
            }`}
          >
            {thread.status}
          </span>
        </div>
      </div>

      {/* Questions list */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {thread.questions.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-500">
            <p className="text-sm">No questions extracted yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {thread.questions.map((q, idx) => {
              const selectedAnswer = q.answers?.find((a) => a.is_selected) || q.answers?.[0]
              const isGenerating = generatingIds.has(q.id)
              const isCopied = copiedIds.has(q.id)
              const isSaved = savedIds.has(q.id) || !!selectedAnswer

              return (
                <div
                  key={q.id}
                  className="p-3 rounded-lg border border-gray-200 dark:border-white/10 bg-white/50 dark:bg-white/5"
                >
                  {/* Question header */}
                  <div className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 text-[10px] font-bold">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-200">
                        {q.question_text}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            q.complexity === 'high'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : q.complexity === 'medium'
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          }`}
                        >
                          {q.complexity}
                        </span>
                        {q.category && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 rounded">
                            {q.category}
                          </span>
                        )}
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            q.status === 'answered'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : q.status === 'matched'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400'
                          }`}
                        >
                          {q.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Answer section */}
                  {isGenerating ? (
                    <div className="mt-3 ml-7">
                      <div className="flex items-center gap-2 text-xs text-indigo-500 dark:text-indigo-400">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Searching knowledge base for answer...</span>
                      </div>
                    </div>
                  ) : selectedAnswer ? (
                    <div className="mt-3 ml-7">
                      <div className="pl-3 border-l-2 border-indigo-200 dark:border-indigo-800">
                        <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                          {selectedAnswer.answer_md}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => handleSaveAnswer(q.id)}
                          className={isSaved ? btnGreen : btnSecondary}
                        >
                          {isSaved ? (
                            <>
                              <Check className="w-3 h-3" />
                              <span>Saved</span>
                            </>
                          ) : (
                            <>
                              <Save className="w-3 h-3" />
                              <span>Save Answer</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleCopyAnswer(q.id, selectedAnswer.answer_md)}
                          className={btnSecondary}
                        >
                          {isCopied ? (
                            <>
                              <Check className="w-3 h-3 text-green-500" />
                              <span>Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleSearchAnswer(q.id)}
                          className={btnSecondary}
                          title="Regenerate answer"
                        >
                          <Search className="w-3 h-3" />
                          <span>Re-search</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 ml-7">
                      <button onClick={() => handleSearchAnswer(q.id)} className={btnPrimary}>
                        <Search className="w-3.5 h-3.5" />
                        <span>Search Answer</span>
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      <div className="px-5 py-3 border-t border-gray-200 dark:border-white/10 flex-shrink-0 bg-gray-50/50 dark:bg-white/5">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleSearchAll}
            disabled={generatingAll}
            className={btnPrimary}
          >
            {generatingAll ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Search className="w-3.5 h-3.5" />
            )}
            <span>{generatingAll ? 'Generating...' : 'Search All Answers'}</span>
          </button>

          {hasAnyAnswers && (
            <>
              <div className="w-px h-5 bg-gray-200 dark:bg-white/10" />
              <button onClick={handleCopyAll} className={btnSecondary}>
                {copiedAll ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                <span>{copiedAll ? 'Copied!' : 'Copy All'}</span>
              </button>
              <button onClick={handleDownloadMd} className={btnSecondary}>
                <Download className="w-3.5 h-3.5" />
                <span>Download MD</span>
              </button>
              <button onClick={handleDownloadDocx} className={btnSecondary}>
                <FileText className="w-3.5 h-3.5" />
                <span>Download Word</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
